import type { PatternPreset } from '~/types/presets';
import type { DevicePattern } from '~/types/midi';
import musicData from '~/data/musicData';
import { PATTERNS, type PatternParts } from '~/types/pattern';

/**
 * Monosynth Default Pattern
 *
 * A driving, rhythmic pattern in D# Minor (Aeolian) with eighth note phrasing.
 * Designed for subtractive synthesis with its punchy, repetitive bass movement.
 *
 * Musical characteristics:
 * - Key: D# Minor/Aeolian (D#, F, F#, G#, A#, C, C#)
 * - Structure: ABCB (4-part form with recurring B section)
 * - Note Length: Eighth notes (8n)
 * - Density: Moderate (35% space probability)
 * - Range: 1 octave (12 semitones) for focused bass/lead lines
 * - Dissonance: 2% (subtle chromatic movement - G natural in part B)
 *
 * Pattern parts:
 * - A: Insistent D#2 repetition (7 notes) with single rest at end
 * - B: Sparse rhythmic phrase (F2, G2) with rests - G2 is chromatic
 * - C: Ascending phrase (G#2 → A#2) with rhythmic variation
 */

const defaultMonoPattern: DevicePattern = {
  rootNote: musicData.rootNotes.find(n => n.note === 'D♯') || musicData.rootNotes[0],
  scale: musicData.scales.find(s => s.id === 'ionian-min') || musicData.scales[0],
  scaleMode: 'Minor',
  pattern: PATTERNS.find(p => p.name === 'ABCB') || PATTERNS[1],
  patternParts: {
    A: [
      { note: 'D#2', duration: '8n' },
      { note: 'D#2', duration: '8n' },
      { note: 'D#2', duration: '8n' },
      { note: 'D#2', duration: '8n' },
      { note: 'D#2', duration: '8n' },
      { note: 'D#2', duration: '8n' },
      { note: 'D#2', duration: '8n' },
      { note: null, duration: '8n' },
    ],
    B: [
      { note: 'F2', duration: '8n' },
      { note: 'F2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: 'G2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: null, duration: '8n' },
      { note: null, duration: '8n' },
      { note: null, duration: '8n' },
    ],
    C: [
      { note: 'G#2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: 'A#2', duration: '8n' },
      { note: 'A#2', duration: '8n' },
      { note: 'A#2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: 'A#2', duration: '8n' },
      { note: null, duration: '8n' },
    ],
    D: [
      { note: 'C#2', duration: '8n' },
      { note: 'C#2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: 'F2', duration: '8n' },
      { note: 'F2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: 'F2', duration: '8n' },
      { note: null, duration: '8n' },
    ],
  } as PatternParts,
  beatsPerBar: 4,
  tempo: 110,
  swing: 0,
  space: 35,
  spaceMin: 20,
  spaceMax: 50,
  dissonance: 2,
  dissonanceMin: 0,
  dissonanceMax: 2,
  spread: 7,
  spreadMin: 0,
  spreadMax: 24,
  range: 12,
  rangeMin: 12,
  rangeMax: 37,
  scrambleToggles: {
    scrambleStructure: true,
    scrambleRootNote: true,
    scrambleMode: true,
    scrambleScale: true,
    scrambleSpace: true,
    scrambleDissonance: true,
    scrambleSpread: true,
    scrambleRange: true,
  },
};

/**
 * Monosynth Default Pattern Preset
 */
export const DEFAULT_MONO_PATTERN_PRESET: PatternPreset = {
  id: 'default-mono-pattern',
  name: 'Default',
  pattern: defaultMonoPattern,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  userId: null, // System preset
  isPublic: true,
};

/**
 * Get the Monosynth default pattern preset
 * Returns a deep copy to avoid mutation
 */
export function getDefaultMonoPattern(): PatternPreset {
  return {
    ...DEFAULT_MONO_PATTERN_PRESET,
    pattern: {
      ...DEFAULT_MONO_PATTERN_PRESET.pattern,
      rootNote: { ...DEFAULT_MONO_PATTERN_PRESET.pattern.rootNote },
      scale: { ...DEFAULT_MONO_PATTERN_PRESET.pattern.scale },
      pattern: { ...DEFAULT_MONO_PATTERN_PRESET.pattern.pattern },
      patternParts: JSON.parse(JSON.stringify(DEFAULT_MONO_PATTERN_PRESET.pattern.patternParts)),
      scrambleToggles: { ...DEFAULT_MONO_PATTERN_PRESET.pattern.scrambleToggles },
    },
  };
}
